import React from "react"
import { useIsEditing, useOverride } from "../editor"
import { Tooltip } from "@kit/design/tooltip"

function SampleContent() {
  return (
    <>
      <div
        style={{
          borderLeft: "1px solid #DCE1E5",
          height: "calc(100% - 20px)",
          position: "absolute",
          top: "11px",
          left: "-12px"
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "0",
            width: "12px",
            height: "1px",
            backgroundColor: "#dce1e5"
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "0",
            width: "12px",
            height: "1px",
            backgroundColor: "#dce1e5"
          }}
        ></div>
      </div>
      <div
        style={{
          backgroundColor: "#F3F6F9",
          border: "1px solid #DCE1E5",
          borderRadius: ".25rem",
          display: "inline-block",
          fontFamily: "var(--font-ui)",
          fontSize: "0.75rem",
          padding: "0.125rem .25rem",
          textAlign: "center"
        }}
      >
        Start of sample content
      </div>
      <h1>Heading 1</h1>
      <p>
        This is a sample of how content will appear in your email. This shows
        how your paragraphs will appear. You can style all of these elements
        with the settings in the sidebar. This content is just an example for
        styling, so it isn&apos;t editable, but it also won&apos;t appear in
        your final email.
      </p>
      <h2>Heading 2</h2>
      <p>
        There are plenty of other elements that you can style in your email. For
        example, this is how{" "}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#" rel="noopener noreferrer">
          a link will appear
        </a>
        . Here is a bulleted list:
      </p>
      <ul>
        <li>Option 1</li>
        <li>Option 2</li>
        <li>Option 3</li>
      </ul>
      <p>And here is a numbered list:</p>
      <ol>
        <li>Option 1</li>
        <li>Option 2</li>
        <li>Option 3</li>
      </ol>
      <h3>Heading 3: Buttons</h3>
      <table width="100%">
        <tbody>
          <tr>
            <td>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className="email-button"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  padding: "6px 24px",
                  maxWidth: "40%",
                  textAlign: "center",
                  textDecoration: "none",
                  width: "fit-content"
                }}
              >
                Button
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <h4>Heading 4: What&apos;s next</h4>

      <p>
        Once you finish styling your template, you can use it in your
        broadcasts. Feel free to upload a logo or other images in the sections
        above or below this example content.
      </p>
      <div
        style={{
          backgroundColor: "#F3F6F9",
          border: "1px solid #DCE1E5",
          borderRadius: ".25rem",
          display: "inline-block",
          fontFamily: "var(--font-ui)",
          fontSize: "0.75rem",
          padding: "0.125rem .25rem",
          textAlign: "center"
        }}
      >
        End of sample content
      </div>
    </>
  )
}

function DefaultContent({ children, showBoundaries }) {
  if (!showBoundaries) return children

  return (
    <>
      {/* Container with left border */}
      <div
        className="border-gray-100 border-l-2 absolute"
        style={{
          height: "calc(100% - 54px)",
          top: "28px",
          left: "-16px"
        }}
      >
        {/* Top horizontal connector */}
        <div
          className="bg-gray-100 absolute top-0"
          style={{
            width: "18px",
            height: "2px"
          }}
        />

        {/* Bottom horizontal connector */}
        <div
          className="bg-gray-100 absolute bottom-0"
          style={{
            width: "18px",
            height: "2px"
          }}
        />
      </div>

      {/* Labels */}
      <div className="bg-gray-100 rounded font-semibold py-1 px-2 my-4 text-xs inline-block">
        Start of default content
        <Tooltip>
          <Tooltip.Trigger
            render={
              <span className="text-xs bg-gray-200 rounded-full ml-2 w-4 h-4 inline-flex items-center justify-center hover:bg-gray-300 cursor-default">
                ?
              </span>
            }
          />
          <Tooltip.Content position="bottom">
            If you add content to this section, it will be used as the starting
            point for broadcasts and sequences that use this template.
          </Tooltip.Content>
        </Tooltip>
      </div>
      {children}
      <div className="bg-gray-100 rounded font-semibold py-1 px-2 my-4 text-xs inline-block">
        End of default content
      </div>
    </>
  )
}

/**
 * In editing mode, this will render the default content region (or the sample
 * content if none is passed in). "{{ message_content }}" will be rendered when
 * not in editing mode.
 */
function Content() {
  const editing = useIsEditing()
  const Region = useOverride("Region")
  const showBoundaries = useOverride("inClassicTemplate")

  return editing ? (
    <div style={{ position: "relative" }}>
      {Region ? (
        <DefaultContent showBoundaries={showBoundaries}>
          <Region name="content" />
        </DefaultContent>
      ) : (
        <SampleContent />
      )}
    </div>
  ) : (
    "{{ message_content }}"
  )
}

export default Content
